import React, { useMemo, useState } from 'react';
import {
  ChevronRight, ChevronDown, File as FileIcon, Folder as FolderIcon,
  FolderOpen, Copy,
} from 'lucide-react';

/**
 * FileTreeExplorer — VS Code style file explorer for a parsed
 * FileSystemTree (the WebContainer-compatible shape produced by
 * `parseMultiFileOutput`).
 *
 * Left  : collapsible folder/file tree.
 * Right : selected file content with monospaced syntax-friendly rendering.
 */

const LANG_BY_EXT = {
  js: 'javascript', mjs: 'javascript', cjs: 'javascript',
  jsx: 'jsx',
  ts: 'typescript', tsx: 'tsx',
  json: 'json',
  html: 'html', htm: 'html',
  css: 'css', scss: 'scss', sass: 'sass',
  md: 'markdown', mdx: 'mdx',
  yml: 'yaml', yaml: 'yaml',
  svg: 'svg', xml: 'xml',
  sh: 'bash',
};

function getLang(path) {
  const dot = path.lastIndexOf('.');
  if (dot === -1) return 'text';
  return LANG_BY_EXT[path.slice(dot + 1).toLowerCase()] || 'text';
}

function flattenTree(tree, prefix = '') {
  // Walks a WebContainer-shape tree into a flat sorted array of
  // { path, isDir, contents? } entries.
  const out = [];
  if (!tree || typeof tree !== 'object') return out;
  const keys = Object.keys(tree).sort((a, b) => {
    const aDir = !!tree[a]?.directory;
    const bDir = !!tree[b]?.directory;
    if (aDir !== bDir) return aDir ? -1 : 1;
    return a.localeCompare(b);
  });
  for (const key of keys) {
    const node = tree[key];
    const path = prefix ? `${prefix}/${key}` : key;
    if (node?.directory) {
      out.push({ path, name: key, isDir: true });
      out.push(...flattenTree(node.directory, path));
    } else if (node?.file) {
      out.push({
        path, name: key, isDir: false,
        contents: node.file.contents || '',
      });
    }
  }
  return out;
}

function pickInitialFile(flat) {
  // Prefer a recognizable entry file for the initial view
  const preferred = ['src/App.jsx', 'src/App.tsx', 'src/App.js', 'src/main.jsx', 'src/main.tsx', 'index.html', 'package.json', 'README.md'];
  for (const p of preferred) {
    const f = flat.find(e => e.path === p && !e.isDir);
    if (f) return f.path;
  }
  return flat.find(e => !e.isDir)?.path || null;
}

export default function FileTreeExplorer({ tree }) {
  const flat = useMemo(() => flattenTree(tree), [tree]);
  const [selected, setSelected] = useState(() => pickInitialFile(flat));
  const [collapsed, setCollapsed] = useState({});
  const [copied, setCopied] = useState(false);

  // When the tree changes (new build), auto-select a fresh initial file.
  React.useEffect(() => {
    const next = pickInitialFile(flat);
    setSelected(next);
    setCollapsed({});
  }, [flat]);

  const fileCount = flat.filter(e => !e.isDir).length;
  const dirCount = flat.filter(e => e.isDir).length;
  const selectedEntry = flat.find(e => e.path === selected && !e.isDir);

  const toggleDir = (path) => setCollapsed(c => ({ ...c, [path]: !c[path] }));

  const handleCopy = () => {
    if (!selectedEntry) return;
    navigator.clipboard.writeText(selectedEntry.contents);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div
      data-testid="file-tree-explorer"
      style={{
        flex: 1,
        display: 'flex',
        minHeight: 0,
        border: '1px solid var(--border-color)',
        background: '#0F0F15',
        fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
      }}
    >
      {/* Left: file tree */}
      <div
        data-testid="file-tree-sidebar"
        style={{
          width: 280,
          minWidth: 220,
          maxWidth: 360,
          borderRight: '1px solid rgba(255,255,255,0.06)',
          background: '#0A0A0F',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '10px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.18em',
          color: 'rgba(255,255,255,0.55)',
          textTransform: 'uppercase',
          fontFamily: 'inherit',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>Explorer</span>
          <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>
            {fileCount} files · {dirCount} dirs
          </span>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          <Tree
            tree={tree}
            prefix=""
            depth={0}
            collapsed={collapsed}
            onToggle={toggleDir}
            selected={selected}
            onSelect={setSelected}
          />
        </div>
      </div>

      {/* Right: file content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: '#13131A' }}>
        {/* Path bar */}
        <div style={{
          padding: '0 14px',
          height: 36,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: '#0F0F15',
        }}>
          <FileIcon size={13} color="rgba(255,255,255,0.45)" />
          <div data-testid="file-tree-path" style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.75)',
            fontFamily: 'inherit',
            flex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {selectedEntry ? selectedEntry.path : '— no file selected —'}
          </div>
          {selectedEntry && (
            <div style={{
              padding: '2px 8px',
              background: 'rgba(124,58,237,0.10)',
              color: '#7C3AED',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              borderRadius: 0,
              border: '1px solid rgba(124,58,237,0.25)',
            }}>
              {getLang(selectedEntry.path)}
            </div>
          )}
          {selectedEntry && (
            <button
              type="button"
              onClick={handleCopy}
              data-testid="file-tree-copy"
              title="Copy file contents"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: copied ? '#7C3AED' : 'rgba(255,255,255,0.65)',
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.15s',
              }}
            >
              <Copy size={11} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>

        {/* File body */}
        <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {selectedEntry ? (
            <CodeBody contents={selectedEntry.contents} />
          ) : (
            <div style={{
              padding: 24,
              color: 'rgba(255,255,255,0.4)',
              fontSize: 13,
              fontFamily: 'inherit',
            }}>
              Select a file from the tree on the left.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Tree({ tree, prefix, depth, collapsed, onToggle, selected, onSelect }) {
  if (!tree || typeof tree !== 'object') return null;
  const keys = Object.keys(tree).sort((a, b) => {
    const aDir = !!tree[a]?.directory;
    const bDir = !!tree[b]?.directory;
    if (aDir !== bDir) return aDir ? -1 : 1;
    return a.localeCompare(b);
  });
  return keys.map(key => {
    const node = tree[key];
    const path = prefix ? `${prefix}/${key}` : key;
    if (node?.directory) {
      const isClosed = !!collapsed[path];
      return (
        <React.Fragment key={path}>
          <Row
            depth={depth}
            isDir
            isClosed={isClosed}
            label={key}
            onClick={() => onToggle(path)}
            selected={false}
            testid={`file-tree-dir-${path}`}
          />
          {!isClosed && (
            <Tree
              tree={node.directory}
              prefix={path}
              depth={depth + 1}
              collapsed={collapsed}
              onToggle={onToggle}
              selected={selected}
              onSelect={onSelect}
            />
          )}
        </React.Fragment>
      );
    }
    if (node?.file) {
      return (
        <Row
          key={path}
          depth={depth}
          isDir={false}
          label={key}
          selected={selected === path}
          onClick={() => onSelect(path)}
          testid={`file-tree-file-${path}`}
        />
      );
    }
    return null;
  });
}

function Row({ depth, isDir, isClosed, label, selected, onClick, testid }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={testid}
      style={{
        padding: `4px 12px 4px ${12 + depth * 14}px`,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
        background: selected
          ? 'rgba(124,58,237,0.10)'
          : hover ? 'rgba(255,255,255,0.04)' : 'transparent',
        color: selected
          ? '#7C3AED'
          : isDir ? 'rgba(255,255,255,0.78)' : 'rgba(255,255,255,0.65)',
        fontSize: 12,
        lineHeight: 1.5,
        fontFamily: 'inherit',
        borderLeft: selected ? '2px solid #7C3AED' : '2px solid transparent',
        userSelect: 'none',
      }}
    >
      {isDir ? (
        <>
          {isClosed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          {isClosed ? <FolderIcon size={12} /> : <FolderOpen size={12} />}
        </>
      ) : (
        <>
          <span style={{ width: 12 }} />
          <FileIcon size={12} />
        </>
      )}
      <span style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>{label}</span>
    </div>
  );
}

function CodeBody({ contents }) {
  // Render with line numbers, monospace, no full syntax-highlighter (we keep
  // it lightweight — a real editor like Monaco would be the next step).
  const lines = (contents || '').split('\n');
  const digits = String(lines.length).length;
  return (
    <div style={{ display: 'flex', minHeight: '100%' }}>
      <pre
        aria-hidden
        style={{
          padding: '12px 8px 12px 14px',
          margin: 0,
          textAlign: 'right',
          color: 'rgba(255,255,255,0.25)',
          background: '#0F0F15',
          fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
          fontSize: 12.5,
          lineHeight: 1.6,
          userSelect: 'none',
          whiteSpace: 'pre',
          minWidth: digits * 10 + 14,
        }}
      >
        {lines.map((_, i) => `${i + 1}\n`).join('')}
      </pre>
      <pre style={{
        flex: 1,
        padding: '12px 16px',
        margin: 0,
        color: '#e6edf3',
        background: '#13131A',
        fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
        fontSize: 12.5,
        lineHeight: 1.6,
        whiteSpace: 'pre',
        overflowX: 'auto',
      }}>
        {contents || ''}
      </pre>
    </div>
  );
}
